import { memo, useMemo, useRef } from 'react';
import { Box3, Vector2, Vector2Tuple, Vector3 } from 'three';
import { Object3D } from 'three';

import {
  CommanderActionTypesEnum,
  TranslateAction,
  TranslateActionPayload,
} from '@spazious/dollhouse-commander';
import {
  DragControls as BaseDragControls,
  DragControlsModifier,
  DragControlsProps as BaseDragControlsProps,
  useDollhouse,
  useInteractiveObjects,
  useSelection,
} from '@spazious/dollhouse-engine';
import {
  DecorationNode,
  FCompositionNode,
  FCompositionNodeImpl,
  FNode,
  FNodeTypesEnum,
  FurnitureNode,
  getNodeFromObject,
  getWorldPosition,
  outlineFromNode,
} from '@spazious/dollhouse-fsetup';
import { NavMeshQuery } from '@spazious/dollhouse-navmesh';
import { join } from '@spazious/dollhouse-selector';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { restrictToAlignedAxis } from '@spazious/dollhouse-three';
import { ControlsModeEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { MainViewMessage } from '../../core/EventManager.js';
import { useCommander, useNodesPlacementValidator } from '../../hooks/index.js';
import { useRestrictedArea } from '../../hooks/useRestrictedArea.js';
import { moveNodesInNavMesh } from '../../utils/index.js';

export type DraggableNodes = FurnitureNode | FCompositionNode | DecorationNode;

const _modifiers: DragControlsModifier[] = [
  (state) => {
    if (state.event.shiftKey) {
      return restrictToAlignedAxis(state);
    }
    return state;
  },
];

type DraggingNode = {
  node: DraggableNodes;
  box: Box3;
  center: Vector2;
  size: Vector2;
  theta: number;
  outline2D?: Vector2Tuple[];
};

const _draggingNodes: DraggingNode[] = [];
const _size = new Vector3();
const _center = new Vector3();

export interface DragControlsProps
  extends Omit<BaseDragControlsProps, 'onDrag' | 'objects' | 'matrixWorld' | 'modifiers'> {
  /**
   * Whether to check against navmesh while dragging nodes.
   * If true, nodes cannot be moved out of navmesh at any time
   * If false, nodes can be moved out of navmesh during drag
   * But, at the end of the drag nodes should alsways be inside navesh, else the drag action is reverted
   * Default: false
   */
  liveNavMeshCheck?: boolean;
}

const draggableNodeTypes = [
  FNodeTypesEnum.Furniture,
  FNodeTypesEnum.FComposition,
  FNodeTypesEnum.Decoration,
];

const getTargetNode = (object: Object3D): DraggableNodes | null => {
  const node = getNodeFromObject(object) as DraggableNodes | null;
  if (!node) {
    return null;
  }
  return node.type === FNodeTypesEnum.Furniture && node.originator ? node.originator : node;
};

const nodeReducer = (acc: DraggableNodes[], node: DraggableNodes | null): DraggableNodes[] => {
  return node && draggableNodeTypes.includes(node.type)
    ? [...acc, node.type === FNodeTypesEnum.Furniture && node.originator ? node.originator : node]
    : acc;
};

const objectNodeReducer = (acc: DraggableNodes[], object: Object3D): DraggableNodes[] => {
  return nodeReducer(acc, getTargetNode(object));
};

export const DragControls: React.FC<DragControlsProps> = memo(
  ({ enabled, liveNavMeshCheck = false, ...restProps }) => {
    const draggedRef = useRef<boolean>(false);
    const actionRef = useRef<TranslateAction | null>(null);
    const payloadRef = useRef<TranslateActionPayload | null>(null);
    const commander = useCommander();
    const { selected: selectedObjects } = useSelection();
    const [interactiveObjects] = useInteractiveObjects();
    const navMesh = useDollhouse(({ world }) => world?.navMesh);
    const { graph } = useGraph();
    const { isPointInRestrictedArea, confirm } = useRestrictedArea();
    const checkRestrictedAreas = useStore(({ settings }) => settings.checkRestrictedAreas);
    const matrixWorld = useDollhouse(({ scene }) => scene.matrixWorld);
    const set = useStore(({ set }) => set);
    const controls = useStore(({ controls }) => controls);

    const navMeshQuery = useMemo(() => (navMesh ? new NavMeshQuery(navMesh) : null), [navMesh]);

    const selectedNodes = useMemo(() => {
      return Array.from(
        new Set(
          [...selectedObjects].reduce<DraggableNodes[]>(objectNodeReducer, [] as DraggableNodes[])
        )
      );
    }, [selectedObjects]);

    const { validator } = useNodesPlacementValidator([]);

    const handleDrag = useEventCallback<PickProp<BaseDragControlsProps, 'onDrag'>>(
      ({ movement, phase, object, event }) => {
        const action = actionRef.current;

        if (phase === 'start') {
          const targetNode = getTargetNode(object);
          const targetNodes = Array.from(
            new Set(
              nodeReducer(
                !(selectedObjects.includes(object) || selectedNodes.includes(targetNode!))
                  ? []
                  : selectedNodes,
                targetNode
              )
            )
          );
          _draggingNodes.length = 0;
          targetNodes.forEach((node) => {
            const dragNode: DraggingNode = {
              node: node,
              box: new Box3(),
              center: new Vector2(...node.position),
              size: new Vector2(),
              theta: node.rotation,
            };
            dragNode.outline2D = outlineFromNode(node);

            if (node.type === FNodeTypesEnum.FComposition) {
              const cNode = node as FCompositionNodeImpl;
              if (cNode.instance?.has('size')) {
                // farrangement nodes, use size attribute
                const size: Vector2Tuple = cNode.instance.getValue('size');
                dragNode.size.fromArray(size);

                dragNode.box.setFromCenterAndSize(
                  _center.set(node.position[0], 0, node.position[1]),
                  _size.set(size[0], 0, size[1])
                );
              } else {
                // fensembles, calculate bounding box
                cNode.instance.computeBoundingBox();
                dragNode.box.copy(cNode.instance.boundingBox!);
              }
            } else if (
              node.type === FNodeTypesEnum.Furniture ||
              node.type === FNodeTypesEnum.Decoration
            ) {
              // furnitures - aleady collider has the bounding box calculated
              const cNode = node as FurnitureNode;
              dragNode.box.copy(cNode.collider!.box);
            }
            dragNode.box.getSize(_size);
            dragNode.size.fromArray([_size.x, _size.z]);
            _draggingNodes.push(dragNode);
          });

          actionRef.current = commander.createAction(
            CommanderActionTypesEnum.Translate,
            join(_draggingNodes.map((node) => node.node.selector))
          );

          // if not selected, move as a group
          const asGroup = !(
            selectedObjects.includes(object) || selectedNodes.includes(targetNode!)
          );

          payloadRef.current = {
            movement: [0, 0],
            copy: !!event.altKey,
            asGroup,
          };
        }

        const { success = true, selectedMovement = movement } = liveNavMeshCheck
          ? moveNodesInNavMesh(_draggingNodes, movement, navMeshQuery!)
          : {};

        if (action && success) {
          payloadRef.current!.movement = [selectedMovement.x, selectedMovement.z];
          action.update(payloadRef.current!);
        }

        if (phase === 'move') {
          if (movement.length() > 0.5 || selectedMovement?.length() > 0.05) {
            set({ tool: ToolModeEnum.None, showNavMesh: true });
          }
          draggedRef.current = true;
        }

        if (action && phase === 'end') {
          if (
            draggedRef.current &&
            (movement.length() > 0.05 || selectedMovement?.length() > 0.05)
          ) {
            const affectedNodes = action
              .affectedNodes()
              .map(({ selector }) => graph.findBySelector(selector))
              .filter((node) => node !== null) as FNode[];
            const success = validator(affectedNodes);
            if (success) {
              const movedNodes = affectedNodes.flatMap((node) => {
                const nodes = [node];
                node?.traverse((cNode) => nodes.push(cNode));
                return nodes;
              }) as FNode[];

              if (
                checkRestrictedAreas &&
                movedNodes.some((node) => isPointInRestrictedArea(getWorldPosition(node)))
              ) {
                confirm((message: MainViewMessage) => {
                  if (message === MainViewMessage.RestrictedAreaActionRevert) {
                    action.abort();
                  } else if (message === MainViewMessage.RestrictedAreaActionIgnore) {
                    action.commit();
                  }
                });
              } else {
                action.commit();
              }
            } else {
              action.abort();
            }
          } else {
            action.abort();
          }
          set({ showNavMesh: false });
          actionRef.current = null;
          draggedRef.current = false;
        }
      }
    );

    return (
      <BaseDragControls
        matrixWorld={matrixWorld}
        modifiers={_modifiers}
        {...restProps}
        enabled={enabled && ![ControlsModeEnum.Walk].includes(controls)}
        objects={interactiveObjects}
        onDrag={handleDrag}
      />
    );
  }
);
